import { ethers } from 'ethers';
import { Token } from '@uniswap/sdk-core';
import { Pool, Position } from '@uniswap/v3-sdk';
import { addressToTokenInfo, NETWORK_ID } from '../config/constants';
import {
  CAMELOT_POSITION_MANAGER,
  getSmartWalletBalanceUSD,
  batchSimulateCollectFees,
  UNISWAP_POSITION_MANAGER,
} from './blockchainService';

// ---------- helpers ----------
export function collectAllPositions(wallets) {
  const allNftIds = [];
  const allPoolAddresses = new Set();
  const seenPositions = new Set();
  wallets.forEach((wallet) => {
    wallet.positions.forEach((pos) => {
      if (pos.nftId && pos.vault && pos.vault.pool) {
        if (!seenPositions.has(pos.nftId)) {
          allNftIds.push(pos.nftId);
          allPoolAddresses.add(pos.vault.pool);
          seenPositions.add(pos.nftId);
        }
      }
    });
  });
  return { allNftIds, allPoolAddresses: Array.from(allPoolAddresses) };
}

// Simple memo caches to avoid repeatedly constructing SDK objects
const tokenCache = new Map(); // key: address -> Token
const poolCache = new Map();  // key: poolAddress -> Pool

function getToken(address, decimals) {
  const key = address.toLowerCase();
  let t = tokenCache.get(key);
  if (!t) {
    t = new Token(NETWORK_ID, address, decimals);
    tokenCache.set(key, t);
  }
  return t;
}

// Use mintAmounts (deposit amounts) to estimate invested value
export function calculateTokenAmounts(liquidity, tickLower, tickUpper, pool, token0Info, token1Info) {
  try {
    if (!token0Info || !token1Info) throw new Error('Missing token info');

    const position = new Position({
      pool,
      liquidity: liquidity.toString(),
      tickLower,
      tickUpper,
    });

    const { amount0, amount1 } = position.mintAmounts;

    const token0Amount = parseFloat(
      ethers.utils.formatUnits(ethers.BigNumber.from(amount0.toString()), token0Info.decimals)
    );
    const token1Amount = parseFloat(
      ethers.utils.formatUnits(ethers.BigNumber.from(amount1.toString()), token1Info.decimals)
    );

    return { amount0: token0Amount, amount1: token1Amount };
  } catch {
    return { amount0: 0, amount1: 0 };
  }
}

// ----- APR config -----
const ESTIMATED_EARN_WINDOW_DAYS = 7; // fallback window if createdAt is missing
const SEC_PER_YEAR = 31_536_000;
const SEC_PER_DAY  = 86_400;

// Reusable APR calculator (JS). Same math + logs as your example, with wallet tag.
export function calculateAprForPosition({
  feesUSD,
  investedUSD,
  createdAtSec,
  walletAddress, // optional: for logging
  nowSec = Math.floor(Date.now() / 1000),
}) {
  const SEC_PER_YEAR_ = (typeof SEC_PER_YEAR !== 'undefined') ? SEC_PER_YEAR : 31536000;
  const SEC_PER_DAY_  = (typeof SEC_PER_DAY  !== 'undefined') ? SEC_PER_DAY  : 86400;
  const EARN_WIN_DAYS_ = (typeof ESTIMATED_EARN_WINDOW_DAYS !== 'undefined') ? ESTIMATED_EARN_WINDOW_DAYS : 7;

  const hasCreated = !!createdAtSec && createdAtSec > 0;
  const durationInSeconds = hasCreated
    ? Math.max(1, nowSec - createdAtSec)
    : EARN_WIN_DAYS_ * SEC_PER_DAY_;

  const durationInDays = durationInSeconds / SEC_PER_DAY_;

  // Same formula you use elsewhere
  const perSecondAPY    = investedUSD > 0 ? (feesUSD / investedUSD) / durationInSeconds : 0;
  const yearlyAPY       = perSecondAPY * SEC_PER_YEAR_;       // fraction
  const dailyReturnRate = investedUSD > 0 ? (feesUSD / investedUSD) / durationInDays : 0;
  const dailyReturnUSD  = durationInDays > 0 ? feesUSD / durationInDays : 0;
  const annualizedAPR   = dailyReturnRate * 365;             // fraction

  // Console logs in your style
  // console.log("=== APR Calculation Verification ===");
  // if (walletAddress) console.log("APR Calculation: Smart wallet:", walletAddress);
  // console.log("APR Calculation: Fees earned:", feesUSD);
  // console.log("APR Calculation: Investment:", investedUSD);
  // console.log("APR Calculation: Days:", durationInDays);
  // console.log("APR Calculation: Daily return rate:", (dailyReturnRate * 100).toFixed(4) + "%");
  // console.log("APR Calculation: Annualized APR (manual):", (annualizedAPR * 100).toFixed(2) + "%");
  // console.log("APR Calculation: Yearly APY (formula):", (yearlyAPY * 100).toFixed(2) + "%");
  // console.log("=====================================");

  // Important: keep raw percent for aggregation; only round for display
  const aprRawPct   = yearlyAPY * 100;                // no rounding
  const aprShownPct = Number(aprRawPct.toFixed(2));   // for display only

  return {
    aprRawPct,            // % (unrounded) -> use this for sums/averages
    aprShownPct,          // % rounded for display
    yearlyAPY,            // fraction
    dailyReturnRate,      // fraction/day
    dailyReturnUSD,       // USD/day
    annualizedAPR,        // fraction
    durationInSeconds,
    durationInDays,
    walletAddress,        // echo for tracing
  };
}

export async function processPositions(wallets, positionsData, poolStates, allPrices) {
  const analyticsThreshold = 10;
  const aboveThresholdWallets = [];

  let walletsWithPositionsAbove10 = 0;
  let totalPositionsAbove10 = 0;
  let totalValueOfPositionsAbove10 = 0;
  let totalPositionsFromQualifiedWallets = 0;
  let totalValueFromQualifiedWallets = 0;
  let totalInactiveValueFromQualifiedWallets = 0;
  let skippedPositions = 0;

  // Global value-weighted APR across all positions (percent) using raw (unrounded) apr
  let globalAprWeightedSum = 0; // Σ (aprRaw% * valueUSD)
  let globalValueSum       = 0; // Σ valueUSD

  // console.log(`\n=== Processing ${wallets.length} wallets for APR ===`);

  // ---------- OPTIMIZATION: Batch all fee collection requests upfront ----------
  const feeRequests = [];
  const requestMetadata = new Map(); // nftId -> {wallet, pos, posData}

  for (const wallet of wallets) {
    for (const pos of wallet.positions) {
      const nftId = pos.nftId;
      const posData = positionsData[nftId];

      if (!posData || !posData.liquidity || posData.liquidity.toString() === '0') {
        continue;
      }

      const positionManager = posData.protocol === 'camelot'
        ? CAMELOT_POSITION_MANAGER
        : UNISWAP_POSITION_MANAGER;

      feeRequests.push({
        positionManager,
        walletAddress: wallet.account,
        tokenId: nftId,
      });

      requestMetadata.set(nftId, { wallet, pos, posData });
    }
  }

  // Batch fetch all fees at once (significantly reduces RPC calls)
  const feeResults = await batchSimulateCollectFees(feeRequests);

  // First pass: calculate wallet values to identify qualifying wallets
  const walletData = new Map(); // wallet.account -> computed data

  for (let i = 0; i < wallets.length; i++) {
    const wallet = wallets[i];

    let totalWalletValueUSD = 0;
    let totalPositionsInWallet = 0;

    // per-wallet counters
    let walletActiveCount = 0;
    let walletInactiveCount = 0;
    let walletInactiveValueUSD = 0;

    // per-wallet APR aggregation (use raw, unrounded)
    let walletAprSumRaw = 0;   // sum of aprRawPct across positions (no rounding)
    let walletAprCount  = 0;   // number of positions contributing to sum

    // per-wallet positions (for APR aggregation/UI)
    const walletPositions = [];

    // console.log(`\n>>> Wallet ${wallet.account} — positions: ${wallet.positions?.length || 0}`);

    for (const pos of wallet.positions) {
      try {
        const nftId = pos.nftId;
        const vault = pos.vault;
        const poolAddress = vault.pool;

        // console.log(`\n--- Position ${nftId} (pool ${poolAddress}) ---`);

        const posData = positionsData[nftId];
        if (!posData) { console.warn("No posData; skipping"); skippedPositions++; continue; }

        const liquidity = posData.liquidity;
        if (!liquidity || liquidity.toString() === '0') { console.warn("Zero liquidity; skipping"); skippedPositions++; continue; }

        totalPositionsInWallet++;

        const poolState = poolStates[poolAddress];
        if (!poolState) { console.warn("No poolState; skipping"); skippedPositions++; continue; }

        const token0Info = addressToTokenInfo[vault.token0.toLowerCase()];
        const token1Info = addressToTokenInfo[vault.token1.toLowerCase()];
        if (!token0Info || !token1Info) { console.warn("Missing token info; skipping"); skippedPositions++; continue; }

        // Construct or reuse Pool instance per pool address
        let pool = poolCache.get(poolAddress);
        if (!pool) {
          const token0 = getToken(vault.token0, token0Info.decimals);
          const token1 = getToken(vault.token1, token1Info.decimals);
          const fee = posData.fee || poolState.fee;
          pool = new Pool(
            token0,
            token1,
            fee,
            poolState.sqrtPriceX96,
            poolState.poolLiquidity,
            poolState.tickCurrent
          );
          poolCache.set(poolAddress, pool);
        }

        // console.log(`Tick range [${posData.tickLower}, ${posData.tickUpper}] | pool tick ${poolState.tickCurrent}`);
        // console.log(`Liquidity: ${liquidity.toString()}`);

        // Invested USD using mintAmounts (deposit value)
        const { amount0, amount1 } = calculateTokenAmounts(
          liquidity,
          posData.tickLower,
          posData.tickUpper,
          pool,
          token0Info,
          token1Info
        );
        if (amount0 === 0 && amount1 === 0) { console.warn("Computed deposit = 0/0; skipping"); skippedPositions++; continue; }

        const price0 = allPrices[vault.token0.toLowerCase()]?.usd || 0;
        const price1 = allPrices[vault.token1.toLowerCase()]?.usd || 0;
        // console.log("Token0 price:", price0, "Token1 price:", price1);

        const investedAmountUSD = amount0 * price0 + amount1 * price1;
        // console.log("Invested amount in USD:", investedAmountUSD);
        totalWalletValueUSD += investedAmountUSD;

        const inRange =
          poolState.tickCurrent >= posData.tickLower &&
          poolState.tickCurrent <  posData.tickUpper;
        if (inRange) {
          walletActiveCount += 1;
        } else {
          walletInactiveCount += 1;
          walletInactiveValueUSD += investedAmountUSD;
        }

        // ---------- fees via simulated collect (unclaimed) - use pre-fetched batch results ----------
        const feeResult = feeResults.get(nftId);
        if (!feeResult) {
          console.warn("No fee result for nftId", nftId, "; skipping");
          skippedPositions++;
          continue;
        }

        const sim0BN = feeResult.amount0;
        const sim1BN = feeResult.amount1;
        // console.log("Accumulated amount0 (raw):", sim0BN.toString(), "Accumulated amount1 (raw):", sim1BN.toString());

        const unclaimed0 = Number(ethers.utils.formatUnits(sim0BN, token0Info.decimals));
        const unclaimed1 = Number(ethers.utils.formatUnits(sim1BN, token1Info.decimals));
        // console.log("Collect fees result (human):", unclaimed0, unclaimed1);

        const collected0 = pos.collectedFee0
          ? Number(ethers.utils.formatUnits(pos.collectedFee0, token0Info.decimals))
          : 0;
        const collected1 = pos.collectedFee1
          ? Number(ethers.utils.formatUnits(pos.collectedFee1, token1Info.decimals))
          : 0;

        const totalFeesUSD =
          unclaimed0 * price0 + unclaimed1 * price1 +
          collected0 * price0 + collected1 * price1;

        // console.log("Total fees in USD:", totalFeesUSD);

        // ---------- APR (via separate function) ----------
        // Normalize createdAt to seconds (ms -> s if needed)
        const rawCreated = Number(pos.createdAt ?? pos.vault?.createdAt ?? 0);
        const createdAtSec = rawCreated > 1e12 ? Math.floor(rawCreated / 1000) : rawCreated;

        const {
          aprRawPct,      // use this for sums/averages
          aprShownPct,    // only for display
        } = calculateAprForPosition({
          feesUSD: totalFeesUSD,
          investedUSD: investedAmountUSD,
          createdAtSec,
          walletAddress: wallet.account,
        });

        // console.log(wallet.account, ":", {
        //   yearlyAPY,
        //   dailyReturnRate,
        //   dailyReturnUSD,
        //   annualizedAPR,
        //   durationInSeconds,
        //   durationInDays,
        // }, "APR inputs/outputs");

        // Global value-weighted aggregation (use raw, unrounded)
        globalAprWeightedSum += (aprRawPct || 0) * (investedAmountUSD || 0);
        globalValueSum       += (investedAmountUSD || 0);

        // per-wallet APR sum (use raw, unrounded)
        walletAprSumRaw += (aprRawPct || 0);
        walletAprCount  += 1;

        // console.log(
        //   wallet.account, ":", aprRawPct, globalAprWeightedSum, globalValueSum,
        //   "APR SUM RAW | sum:", walletAprSumRaw.toFixed(6),
        //   "| count:", walletAprCount,
        //   "| simple avg:", (walletAprSumRaw / walletAprCount).toFixed(6) + "%"
        // );

        walletPositions.push({
          nftId,
          valueUSD: Number(investedAmountUSD.toFixed(4)),
          feesUSD: Number(totalFeesUSD.toFixed(4)),
          aprPercent: aprShownPct,   // display value
          active: inRange,
        });

        if (investedAmountUSD >= analyticsThreshold) {
          totalPositionsAbove10++;
          totalValueOfPositionsAbove10 += investedAmountUSD;
        }
      } catch (e) {
        console.warn('Position processing failed, skipping. Error:', e?.message || e);
        skippedPositions++;
      }
    }

    if (totalWalletValueUSD >= analyticsThreshold) {
      // Wallet APR aggregation (percent)
      // Weighted (use display aprPercent? better use raw for accuracy but keep display as 2-decimal)
      let aprWeightedSum = 0;
      let valueSum = 0;
      for (const p of walletPositions) {
        // We only stored display aprPercent; derive raw-ish from it by not re-using here
        // Keep weighted using the raw sums we already tracked
        aprWeightedSum += (p.aprPercent || 0) * (p.valueUSD || 0);
        valueSum       += (p.valueUSD || 0);
      }
      const walletAPRWeighted   = valueSum > 0 ? (aprWeightedSum / valueSum) : 0; // %
      const walletAPRUnweighted = walletAprCount > 0 ? (walletAprSumRaw / walletAprCount) : 0; // use raw avg
      const walletAPR           = walletAPRUnweighted;

      // Log the per-wallet SUM too
      // console.log(
      //   `Wallet ${wallet.account}: APR sumRaw=${walletAprSumRaw.toFixed(6)} | count=${walletAprCount} | simple avg=${walletAPR.toFixed(2)}% | weighted=${walletAPRWeighted.toFixed(2)}%`
      // );

      // Store wallet data for batch balance fetching
      walletData.set(wallet.account, {
        wallet: wallet.account,
        totalWalletValueUSD,
        totalPositionsInWallet,
        walletActiveCount,
        walletInactiveCount,
        walletInactiveValueUSD,
        walletAPR,
        walletAPRWeighted,
        walletAprSumRaw,
        walletAprCount,
        walletPositions,
      });

      walletsWithPositionsAbove10++;
      totalPositionsFromQualifiedWallets += totalPositionsInWallet;
      totalValueFromQualifiedWallets     += totalWalletValueUSD;
      totalInactiveValueFromQualifiedWallets += walletInactiveValueUSD;
    }
  }

  // ---------- OPTIMIZATION: Batch fetch dust balances for all qualifying wallets ----------
  const qualifyingWalletAddresses = Array.from(walletData.keys());
  const dustBalances = new Map(); // wallet address -> dustUSD

  // Batch process dust balances with concurrency
  const dustConcurrency = 5;
  for (let i = 0; i < qualifyingWalletAddresses.length; i += dustConcurrency) {
    const batch = qualifyingWalletAddresses.slice(i, i + dustConcurrency);
    const results = await Promise.all(
      batch.map(addr =>
        getSmartWalletBalanceUSD(addr, allPrices, addressToTokenInfo)
          .then(dustUSD => ({ addr, dustUSD }))
          .catch(() => ({ addr, dustUSD: 0 }))
      )
    );
    results.forEach(({ addr, dustUSD }) => dustBalances.set(addr, dustUSD));
  }

  // Second pass: build final results with dust balances
  for (const [walletAddr, data] of walletData.entries()) {
    const dustUSD = dustBalances.get(walletAddr) || 0;
    const totalWalletWithDust = data.totalWalletValueUSD + dustUSD;

    aboveThresholdWallets.push({
      wallet: data.wallet,
      valueUSD: parseFloat(data.totalWalletValueUSD.toFixed(4)),
      positionCount: data.totalPositionsInWallet,
      activeCount: data.walletActiveCount,
      inactiveCount: data.walletInactiveCount,
      dustValueUSD: parseFloat(dustUSD.toFixed(4)),
      totalValueUSD: parseFloat(totalWalletWithDust.toFixed(4)),
      totalAPR: Number(data.walletAPR.toFixed(2)),
      totalAPRWeighted: Number(data.walletAPRWeighted.toFixed(2)),
      totalAPRUnweighted: Number(data.walletAPR.toFixed(2)),
      totalAPRSum: Number(data.walletAprSumRaw.toFixed(6)),
      aprPositionsCount: data.walletAprCount,
      positions: data.walletPositions,
    });
  }

  // Global value-weighted average APR across all positions (in %), using raw (unrounded)
  const averageAPR = globalValueSum > 0 ? (globalAprWeightedSum / globalValueSum) : 0;
  // console.log('Global average APR (value-weighted):', averageAPR.toFixed(2) + '%');

  return {
    walletsWithPositionsAbove10,
    totalPositionsAbove10,
    totalValueOfPositionsAbove10,
    totalPositionsFromQualifiedWallets,
    totalValueFromQualifiedWallets,
    totalInactiveValueFromQualifiedWallets,
    aboveThresholdWallets: aboveThresholdWallets.sort((a, b) => b.valueUSD - a.valueUSD),
    skippedPositions,
    averageAPR: Number(averageAPR.toFixed(2)), // %
  };
}